/**
 * The quiet line on an account's Items to review tab (TMMAPP-0163 item 12): "Is this {name}?" with
 * Yes and No. The answers are `useInstitutionAnswer`'s.
 */
import type { MouseEvent } from 'react';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Account, InstitutionProposal } from '@/api/accounts';
import { SubscriberActionHint, type SubscriberAction } from '@/session/useSubscriberAction';
import { resolveTokens } from '@/theme/theme';
import { useInstitutionAnswer, type InstitutionAnswerOptions } from './useInstitutionAnswer';

interface InstitutionProposalLineProps extends InstitutionAnswerOptions {
  account: Account;
  proposal: InstitutionProposal;
}

/** The quiet line on the account's Items to review tab: "Is this {name}?" with Yes and No. */
export function InstitutionProposalLine({
  account,
  proposal,
  onAnswered,
  onNotify,
}: InstitutionProposalLineProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const { busy, yes, no } = useInstitutionAnswer(account, proposal, { onAnswered, onNotify });
  return (
    <Box
      data-institution-proposal=""
      sx={{
        border: `1px solid ${tokens.line}`,
        borderRadius: `${tokens.radius.control}px`,
        p: '10px 14px',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ gap: 1, alignItems: { xs: 'flex-start', sm: 'center' } }}
      >
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
          {t('institutions.proposal.question', { name: proposal.name })}
        </Typography>
        <Stack direction="row" sx={{ gap: 1 }}>
          <AnswerButton
            label={t('institutions.proposal.yes')}
            name={t('institutions.proposal.yesName', { name: proposal.name })}
            action={yes}
            busy={busy}
          />
          <AnswerButton
            label={t('institutions.proposal.no')}
            name={t('institutions.proposal.noName', { name: proposal.name })}
            action={no}
            busy={busy}
          />
        </Stack>
      </Stack>
    </Box>
  );
}

function AnswerButton({
  label,
  name,
  action,
  busy,
}: {
  label: string;
  /** The accessible name, which starts with the visible label and names the bank. */
  name: string;
  action: SubscriberAction;
  busy: boolean;
}) {
  return (
    <>
      <Button
        size="small"
        variant="text"
        aria-label={name}
        aria-describedby={action.descriptionId}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          if (!busy) action.run(event);
        }}
      >
        {label}
      </Button>
      <SubscriberActionHint id={action.descriptionId} description={action.description} />
    </>
  );
}
